import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import driveApi, { errorMessage } from '../api/drive';
import syncApi from '../api/sync';

const DriveContext = createContext(null);

export const useDrive = () => useContext(DriveContext);

const UPLOAD_CONCURRENCY = 3;
let uploadSeq = 0;

/**
 * App-wide drive state: the drive list, a "something changed" counter that
 * views listen to, toasts, and the upload queue.
 */
const DriveProvider = ({ children }) => {
  const [drives, setDrives] = useState([]);
  const [drivesLoaded, setDrivesLoaded] = useState(false);
  const [version, setVersion] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [uploads, setUploads] = useState([]);
  // Folder currently open in the browser: where "New" and drops put things
  const [location, setLocation] = useState(null);
  // Has this person signed in the desktop app somewhere? (enables "Open on this computer")
  const [hasDesktop, setHasDesktop] = useState(false);

  useEffect(() => {
    syncApi.devices().then((d) => setHasDesktop(d.length > 0)).catch(() => {});
  }, []);
  const queue = useRef([]);
  const active = useRef(0);
  const controllers = useRef({});

  const refreshDrives = useCallback(async () => {
    try {
      setDrives(await driveApi.drives());
    } finally {
      setDrivesLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshDrives();
  }, [refreshDrives]);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const toast = useCallback((message, tone = 'default') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((list) => [...list, { id, message, tone }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 4200);
  }, []);

  const patchUpload = (id, patch) =>
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const pump = useCallback(() => {
    while (active.current < UPLOAD_CONCURRENCY && queue.current.length) {
      const job = queue.current.shift();
      active.current += 1;
      patchUpload(job.id, { status: 'uploading' });
      driveApi
        .upload({
          drive: job.drive,
          parent: job.parent,
          file: job.file,
          relativePath: job.relativePath,
          signal: job.controller.signal,
          onProgress: (progress) => patchUpload(job.id, { progress }),
        })
        .then(() => {
          patchUpload(job.id, { status: 'done', progress: 1 });
          bump();
        })
        .catch((err) => {
          const cancelled = err?.code === 'ERR_CANCELED';
          patchUpload(job.id, { status: cancelled ? 'cancelled' : 'error', error: errorMessage(err, 'Upload failed') });
        })
        .finally(() => {
          active.current -= 1;
          pump();
        });
    }
  }, [bump]);

  /** files: [{ file, relativePath }] */
  const uploadFiles = useCallback((files, { drive, parent }) => {
    const jobs = files.map(({ file, relativePath }) => ({
      id: ++uploadSeq,
      name: relativePath || file.name,
      size: file.size,
      file,
      relativePath,
      drive,
      parent,
      progress: 0,
      status: 'queued',
      controller: new AbortController(),
    }));
    setUploads((list) => [...list, ...jobs.map((j) => ({
      id: j.id, name: j.name, size: j.size, progress: 0, status: 'queued',
    }))]);
    queue.current.push(...jobs);
    jobs.forEach((j) => { controllers.current[j.id] = j.controller; });
    pump();
  }, [pump]);

  const cancelUpload = useCallback((id) => {
    queue.current = queue.current.filter((j) => j.id !== id);
    controllers.current[id]?.abort();
    patchUpload(id, { status: 'cancelled' });
  }, []);

  const clearUploads = useCallback(() => {
    setUploads((list) => list.filter((u) => u.status === 'uploading' || u.status === 'queued'));
  }, []);

  const value = useMemo(() => ({
    drives,
    drivesLoaded,
    personalDrive: drives.find((d) => d.kind === 'personal'),
    sharedDrives: drives.filter((d) => d.kind === 'shared'),
    refreshDrives,
    version,
    bump,
    toast,
    toasts,
    uploads,
    uploadFiles,
    cancelUpload,
    clearUploads,
    location,
    setLocation,
    hasDesktop,
  }), [hasDesktop, drives, drivesLoaded, refreshDrives, version, bump, toast, toasts, uploads, uploadFiles, cancelUpload, clearUploads, location]);

  return <DriveContext.Provider value={value}>{children}</DriveContext.Provider>;
};

export default DriveProvider;
